import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom, map, Observable, startWith } from 'rxjs';

import { MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import { MAT_DIALOG_DATA } from '@angular/material/dialog';

// Enums
import { BusinessSectorDescriptor } from '@app/enums/business-sector';
import { CountryDescriptor } from '@app/enums/country';
import { EntityTypeDescriptor } from '@app/enums/entity-type';
import { EntitySizeDescriptor, ESAdditionalInformationDescriptor } from '@app/enums/entity-size';
import { NumberOfEmployeesDescriptor } from '@app/enums/number-of-employees';

// Models
import { EntityModel } from '@app/models/entity.model';

// Services
import { EntityService } from '@app/services/entity/entity.service';

// Utils
import {
  mapEnumDescriptorToMultiselectOptions,
  MenuItemInterface,
  sortAlphabetical,
} from '@app/utils/input-fields/input-fields.utils';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BaseModalComponent } from '../../../../../components/base-modal/base-modal.component';

export type SearchMode = 'id' | 'companyName';

@Component({
  standalone: true,
  selector: 'app-entity-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    MatRadioModule,
    MatTableModule,
    FormsModule,
    MatProgressSpinnerModule,
    BaseModalComponent,
  ],
  templateUrl: './entity-edit-modal.component.html',
  styleUrl: './entity-edit-modal.component.scss',
})
export class EntityEditModalComponent implements OnInit {
  public searchByDnBIdFormData: FormGroup;
  public searchByCompanyNameFormData: FormGroup;

  public filteredCountries$: Observable<MenuItemInterface[]> | undefined;
  public searchMode?: SearchMode = 'companyName';
  public isLoading = false;
  public activeEntity?: EntityModel;

  public companyMatches = new MatTableDataSource<EntityModel>();

  // Company matches columns to display
  public companyMatchColumns: (keyof EntityModel | 'selectRadio')[] = [
    'selectRadio',
    'name',
    'countryCode',
    'status',
    'dnbId',
    'revenue',
    'city',
    'zipCode',
    'headquarterAddress',
    'domain',
    'ultimateParentCompanyName',
  ];

  public entityTypeOptions = mapEnumDescriptorToMultiselectOptions(EntityTypeDescriptor);
  public entitySizeOptions = mapEnumDescriptorToMultiselectOptions(EntitySizeDescriptor);
  public countryOptions = sortAlphabetical(mapEnumDescriptorToMultiselectOptions(CountryDescriptor));
  public businessSectorOptions = sortAlphabetical(mapEnumDescriptorToMultiselectOptions(BusinessSectorDescriptor));
  public numberOfEmployeesOptions = mapEnumDescriptorToMultiselectOptions(NumberOfEmployeesDescriptor);

  // Tooltips
  public entitySizeTooltipDescriptor = Object.keys(ESAdditionalInformationDescriptor)
    .map((k) => ({ [k]: (ESAdditionalInformationDescriptor as Record<string, string>)[k] }))
    .reduce((acc, next) => ({ ...acc, ...next }), {});

  // Getters
  public get searchByDnbIdInvalid(): boolean {
    return this.searchByDnBIdFormData.get('dnbId')?.invalid ?? false;
  }

  public get searchByCompanyNameInvalid(): boolean {
    return this.searchByCompanyNameFormData.get('companyName')?.invalid ?? false;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<EntityEditModalComponent, EntityModel | null>,
    private entityService: EntityService,
    @Inject(MAT_DIALOG_DATA) public entityData: EntityModel | null,
  ) {
    this.searchByDnBIdFormData = this.fb.group({
      dnbId: ['', Validators.required],
    });

    this.searchByCompanyNameFormData = this.fb.group({
      companyName: ['', Validators.required],
      country: [''],
      domain: [''],
    });
  }

  ngOnInit(): void {
    if (this.entityData) {
      // Populate search form data
      this.searchByDnBIdFormData.get('dnbId')?.setValue(this.entityData.dnbId);
      this.searchByCompanyNameFormData.get('companyName')?.setValue(this.entityData.name);
      this.searchByCompanyNameFormData.get('domain')?.setValue(this.entityData.website);

      if (this.entityData.countryCode) {
        this.searchByCompanyNameFormData.get('country')?.setValue(this.entityData.countryCode);
      }
    }

    // Autocomplete for country
    this.filteredCountries$ = this.searchByCompanyNameFormData.get('country')!.valueChanges.pipe(
      startWith(''),
      map((searchTerm) => {
        if (!searchTerm) return this.countryOptions;

        const filterValue = searchTerm.toLowerCase();

        return this.countryOptions.filter((country) => country.label.toLowerCase().includes(filterValue));
      }),
    );
  }

  // Used to display the country name instead of the country code in the input.
  public displayCountryName(countryCode: string): string {
    if (!countryCode) return '';
    const country = this.countryOptions.find((c) => c.value === countryCode);
    return country ? country.label : countryCode;
  }

  // Used to clear the data from the table when a different mode is selected.
  public searchModeSelected(): void {
    this.companyMatches.data = [];
    this.activeEntity = undefined;
  }

  // Executing the search action, deciding which search to perform depending on the selected mode.
  public async search(): Promise<void> {
    this.isLoading = true;

    if (this.searchMode === 'id') {
      await this.searchByDnBId();
    } else if (this.searchMode === 'companyName') {
      await this.searchByCompanyName();
    }

    this.isLoading = false;
  }

  public async searchByDnBId(): Promise<void> {
    const dnbValue = this.searchByDnBIdFormData.get('dnbId')?.value ?? '';

    try {
      const responseEntityModel = (await firstValueFrom(this.entityService.findEntityByDnb(dnbValue))) as EntityModel;

      // populate the results table shown in the UI with only one record. That is the expected behavior in this search mode
      this.companyMatches.data = [responseEntityModel];
      this.activeEntity = responseEntityModel;

      // Entity is directly loaded as selected and can be saved.
    } catch {
      this.companyMatches.data = [];
      this.activeEntity = undefined;
    }
  }

  public async searchByCompanyName(): Promise<void> {
    const companyNameFormField = this.searchByCompanyNameFormData.get('companyName')?.value ?? '';
    const countryFormField = this.searchByCompanyNameFormData.get('country')?.value ?? '';
    const domainFormField = this.searchByCompanyNameFormData.get('domain')?.value ?? '';
    try {
      this.companyMatches.data = await firstValueFrom(
        this.entityService.findCompanyMatches(companyNameFormField, countryFormField, domainFormField),
      );
    } catch {
      this.companyMatches.data = [];
      this.activeEntity = undefined;
    }
  }

  public saveEntity(): void {
    this.dialogRef.close(this.activeEntity);
  }

  public cancelEntityEdit(): void {
    this.dialogRef.close();
  }

  public onRowSelected(entity: EntityModel): void {
    this.activeEntity = entity;
  }
}
